/**
 * Parses Cloudinary dimension string (e.g., 'w_300,h_300' or 'w_300') into structured parameters
 */
function parseCloudinaryDimensions(dimensions: string): { width?: number; height?: number; quality?: number; mode?: string } {
    if (!dimensions) {
        return {};
    }

    const params: { width?: number; height?: number; quality?: number; mode?: string } = {};
    const parts = dimensions.split(',');

    parts.forEach(part => {
        const trimmed = part.trim();
        if (trimmed.startsWith('w_')) {
            const width = parseInt(trimmed.substring(2), 10);
            if (!isNaN(width)) {
                params.width = width;
            }
        } else if (trimmed.startsWith('h_')) {
            const height = parseInt(trimmed.substring(2), 10);
            if (!isNaN(height)) {
                params.height = height;
            }
        } else if (trimmed.startsWith('q_')) {
            const quality = parseInt(trimmed.substring(2), 10);
            if (!isNaN(quality)) {
                params.quality = quality;
            }
        } else if (trimmed.startsWith('c_')) {
            params.mode = trimmed.substring(2);
        }
    });

    return params;
}

/**
 * Transforms image URLs for both Cloudinary and Cloudflare R2
 * @param link - Image URL to transform
 * @param quality - Image quality (1-100)
 * @param width - Image width in pixels
 * @param height - Image height in pixels
 * @param mode - Transformation mode ('scale', 'fill', etc.)
 * @returns Transformed image URL
 */
export function transformImage(link: string, quality?: number, width?: number, height?: number, mode?: string): string {
    if (!link || link === 'default') {
        return link;
    }

    // Skip transformations for non-image files (PDFs, docs, etc.)
    // This prevents Cloudflare from trying to transform documents as images
    if (!isImageUrl(link)) {
        return link;
    }

    const imageLink = link;
    const isCloudinary =
        imageLink.includes('images.staffy.com') ||
        imageLink.includes('res.cloudinary.com');
    const isCloudFront = imageLink.includes('cloudfront.net');

    // Skip transformations for CloudFront URLs
    if (isCloudFront) {
        return imageLink;
    }

    // Cloudinary transformations (backward compatible)
    if (isCloudinary) {
        let t_width = 'w_auto';
        let t_height = 'f_auto';
        let t_quality = 'q_auto:eco';
        let t_mode = 'c_scale';
        const extension = '.webp';

        if (Number.isInteger(width)) {
            t_width = `w_${width}`;
        }
        if (Number.isInteger(quality)) {
            t_quality = `q_${quality}`;
        }
        if (Number.isInteger(height)) {
            t_height = `h_${height}`;
        }
        if (mode && mode.length > 0) {
            t_mode = `c_${mode}`;
        }
        const transformer = `upload/${t_mode},${t_width},${t_height},${t_quality}/`;
        return (
            imageLink?.split('upload/')[0] +
            transformer +
            imageLink?.split('upload/')[1]?.split('.')[0] +
            extension
        );
    }

    // Cloudflare R2 transformations (new)
    const params: string[] = [];

    if (Number.isInteger(width)) {
        params.push(`width=${width}`);
    }
    if (Number.isInteger(height)) {
        params.push(`height=${height}`);
    }
    if (Number.isInteger(quality)) {
        params.push(`quality=${quality}`);
    }

    // Map Cloudinary modes to Cloudflare fit values
    let fitMode = 'scale-down'; // default
    if (mode === 'fill') {
        fitMode = 'cover';
    } else if (mode === 'scale') {
        fitMode = 'scale-down';
    }
    params.push(`fit=${fitMode}`);
    params.push('format=webp');

    // Extract the path after the domain
    try {
        const url = new URL(imageLink);
        const path = url.pathname;

        // Build Cloudflare R2 URL with transformations
        return `${url.origin}/cdn-cgi/image/${params.join(',')}${path}`;
    } catch (e) {
        // If URL parsing fails, return original link
        console.error('Failed to parse image URL:', imageLink, e);
        return imageLink;
    }
}

/**
 * Transforms image URL using Cloudinary-style dimension string (e.g., 'w_300,h_300')
 * Automatically detects if URL is Cloudinary or Cloudflare R2 and applies appropriate transformations
 * @param imageUrl - Image URL to transform
 * @param dimensions - Cloudinary-style dimension string (e.g., 'w_300,h_300', 'w_250,h_200')
 * @returns Transformed image URL
 */
export function transformImageWithDimensions(imageUrl: string, dimensions?: string): string {
    if (!imageUrl || imageUrl === 'default') {
        return imageUrl;
    }

    // Parse Cloudinary dimension string into structured parameters
    const params = parseCloudinaryDimensions(dimensions || '');

    // Apply transformations using the main transform function
    return transformImage(imageUrl, params.quality, params.width, params.height, params.mode);
}

export function dataURLtoFile(dataurl: string, filename: string) {
    const arr = dataurl.split(','),
        mime = arr[0].match(/:(.*?);/)[1],
        bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], filename, { type: mime });
}

/**
 * Detects file extension from URL by searching anywhere in the path
 * Handles both standard URLs and migrated R2 URLs where extension may not be at the end
 * @param url - URL to check
 * @returns File extension (lowercase, without dot) or null if not found
 */
export function getFileExtensionFromUrl(url: string): string | null {
    if (!url) {
        return null;
    }

    // Clean up URL - remove query params and hash
    const cleanUrl = url.split('?')[0].split('#')[0];

    // Check for common file extensions anywhere in the URL path
    const extensionPattern = /\.(pdf|docx|doc|txt|jpg|jpeg|png|gif|bmp|webp|heic|heif)(?=[\/\d\.]|$)/gi;
    const matches = cleanUrl.match(extensionPattern);

    if (matches && matches.length > 0) {
        // Return the last matched extension (most likely the actual file extension)
        const lastMatch = matches[matches.length - 1];
        return lastMatch.substring(1).toLowerCase(); // Remove the dot and convert to lowercase
    }

    return null;
}

/**
 * Determines certificate file type from URL or originalCloudinaryUrl
 * @param url - Current URL (possibly migrated R2 URL)
 * @param originalCloudinaryUrl - Original Cloudinary URL before migration (optional)
 * @returns Object with file type flags
 */
export function detectCertificateFileType(url: string, originalCloudinaryUrl?: string): {
    isPDF: boolean;
    isDoc: boolean;
    isImage: boolean;
    extension: string | null;
} {
    // Prefer originalCloudinaryUrl for file type detection if available
    const urlToCheck = originalCloudinaryUrl || url;
    const extension = getFileExtensionFromUrl(urlToCheck);

    const isPDF = extension === 'pdf';
    const isDoc = extension === 'docx' || extension === 'doc' || extension === 'txt';
    const isImage = !isPDF && !isDoc && (
        extension === 'jpg' ||
        extension === 'jpeg' ||
        extension === 'png' ||
        extension === 'gif' ||
        extension === 'bmp' ||
        extension === 'webp' ||
        extension === 'heic' ||
        extension === 'heif'
    );

    return {
        isPDF,
        isDoc,
        isImage: isImage || (!isPDF && !isDoc), // Default to image if type cannot be determined
        extension
    };
}

/**
 * Detects whether a pdf-viewer (pdfjs) load error is caused by a password-protected PDF.
 *
 * pdfjs's PasswordException sets `this.name = this.constructor.name`, which is unreliable
 * once the build is minified (constructor names get mangled), so `error.name` can't be
 * trusted here. `error.code` (PasswordResponses: 1 = NEED_PASSWORD, 2 = INCORRECT_PASSWORD)
 * and `error.message` ("No password given" / "Incorrect Password") are plain literals that
 * survive minification, so they're checked instead.
 */
export function isPdfPasswordError(error: any): boolean {
    if (!error) {
        return false;
    }
    if (error.code === 1 || error.code === 2) {
        return true;
    }
    return typeof error.message === 'string' && /password/i.test(error.message);
}

export function isImageUrl(url: string): boolean {
    if (!url) {
        return false;
    }
    const extension = getFileExtensionFromUrl(url);
    return ['jpeg', 'jpg', 'png', 'gif', 'bmp', 'webp', 'heic', 'heif'].includes(extension || '');
}

export async function base64ToFile(base64String: string, fileName: string): Promise<File> {
    let file = await fetch(base64String);
    const blob = await file.blob();
    return new File([blob], fileName, { type: 'image/png' });
}

function readFileAsDataUrl(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (event: any) => resolve(event.target.result);
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(file);
    });
}

function loadImageFromDataUrl(dataUrl: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
        const image = new Image();
        image.onload = () => resolve(image);
        image.onerror = () => reject(new Error('Unable to decode image'));
        image.src = dataUrl;
    });
}

/**
 * Downscales and re-encodes an image before it is uploaded.
 *
 * Photos taken on a modern phone are routinely 5-12 MB, and pushing them up raw
 * over a mobile connection is what makes the gateway time out (504). Shrinking
 * the longest edge and re-encoding as JPEG brings a typical photo down to a few
 * hundred KB without a noticeable loss for screenshots / bug reports.
 *
 * Anything that is not a decodable raster image (PDFs, HEIC, corrupt files) is
 * returned untouched so the caller behaves exactly as before.
 */
export async function compressImageFile(
    file: File,
    maxDimension: number = 1280,
    quality: number = 0.7
): Promise<File> {
    if (!file || !file.type || file.type.indexOf('image/') !== 0) {
        return file;
    }

    // Browsers cannot draw HEIC/HEIF onto a canvas - leave them as-is.
    if (file.type === 'image/heic' || file.type === 'image/heif') {
        return file;
    }

    try {
        const dataUrl = await readFileAsDataUrl(file);
        const image = await loadImageFromDataUrl(dataUrl);
        const longestEdge = Math.max(image.width, image.height);

        if (!longestEdge) {
            return file;
        }

        const scale = Math.min(1, maxDimension / longestEdge);

        // Already small in both dimensions and bytes - nothing to gain.
        if (scale === 1 && file.size <= 1024 * 1024) {
            return file;
        }

        const canvas = document.createElement('canvas');
        canvas.width = Math.round(image.width * scale);
        canvas.height = Math.round(image.height * scale);

        const context = canvas.getContext('2d');
        if (!context) {
            return file;
        }
        context.drawImage(image, 0, 0, canvas.width, canvas.height);

        const blob = await new Promise<Blob>((resolve) => {
            canvas.toBlob((result) => resolve(result), 'image/jpeg', quality);
        });

        // Re-encoding is not always a win (small PNGs in particular).
        if (!blob || blob.size >= file.size) {
            return file;
        }

        const name = file.name ? file.name.replace(/\.[^.]+$/, '') + '.jpg' : 'attachment.jpg';
        return new File([blob], name, { type: 'image/jpeg' });
    } catch (error) {
        console.error('Failed to compress image, uploading the original file', error);
        return file;
    }
}
